import config from 'config';
import moment from 'moment';
import { deleteCookie, getCookie, responseError } from 'helpers';
import { APIResponse } from 'types/Request';
import {
  AutorizacionIOMA,
  AfiliadoIOMA,
} from 'types/AutorizacionesIOMA';

interface getAllOptions {
  page: number;
  estado: string;
  fechaDesde: string;
  fechaHasta: string;
}

export interface getAllResponse {
  rows: AutorizacionIOMA[],
  count: number,
}

function revokeCredentials() {
  deleteCookie('accessToken');
  deleteCookie('sessionCookie');
  // eslint-disable-next-line no-restricted-globals
  return location.reload();
}

export async function getAllAutorizacionesIOMA(
  matricula: string | null = null,
  options: getAllOptions,
): Promise<getAllResponse> {
  try {
    const url = new URL(`${config.baseUrl}/authorizationsioma/${matricula}`);

    if (!!options.page) url.searchParams.append('page', options.page.toString());

    if (!!options.page) {
      url.searchParams.append('estado', options.estado);
    } else {
      url.searchParams.append('estado', 'pendientes');
    }

    if (!!options.fechaDesde) {
      const fechaDesde = moment(options.fechaDesde).format('YYYY-MM-DD');
      url.searchParams.append('fechaDesde', fechaDesde);
    }

    if (!!options.fechaHasta) {
      const fechaHasta = moment(options.fechaHasta).format('YYYY-MM-DD');
      url.searchParams.append('fechaHasta', fechaHasta);
    }

    type Response = APIResponse<getAllResponse>

    const emptyRes: getAllResponse = {
      rows: [],
      count: 0,
    };

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return emptyRes;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return emptyRes;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
        'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (1)');
    }

    return content.payload ?? emptyRes;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (2)');
  }
}

export async function getAutorizacionIOMAById(
  matricula: string | null = null,
  estado: string,
  id: number,
): Promise<AutorizacionIOMA | null> {
  try {
    const url = new URL(`${config.baseUrl}/authorizationsioma/${estado}/${matricula}/${id}`);

    type Response = APIResponse<AutorizacionIOMA>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return null;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return null;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
        'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (1)');
    }

    return content.payload ?? null;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (2)');
  }
}

export async function getIOMAIsBlocked(): Promise<any | null> {
  try {
    const url = new URL(`${config.baseUrl}/authorizationsioma/is-blocked`);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return null;
    }

    const content = await response.json();

    return content ?? null;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las información. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador.');
  }
}

export async function getAfiliadoIOMAByNro(numeroAfiliado: string): Promise<AfiliadoIOMA | null> {
  try {
    const url = new URL(`${config.baseUrl}/authorizationsioma/afiliado/${numeroAfiliado}`);

    type Response = APIResponse<AfiliadoIOMA>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      return null;
    }

    const content = await response.json() as Response;

    if (content.status !== 'ok') {
      if (content.code === 'CONNECTION_ERROR') {
        return null;
      }

      throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
        'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (1)');
    }

    return content.payload ?? null;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de intentar recuperar las prestaciones. ' +
      'Temporalmente no se podrá enviar la solicitud pero si guardar como Borrador. (2)');
  }
}

export async function postAutorizacionIOMA(
  data: Partial<AutorizacionIOMA>,
  // files: Record<string, File>,
): Promise<any | undefined> {
  try {
    const formData = new FormData();

    /*
    Object.entries(files).forEach(([name, value]) => {
      formData.append(name, value);
    });
    */

    formData.append('metadata', JSON.stringify(data));

    const res = await fetch(`${ config.baseUrl }/authorizationsioma`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: getCookie('accessToken'),
      },
      body: formData,
    });

    if (res.status === 400) return responseError();

    if (res.status === 401 && true) {
      return revokeCredentials();
    }

    return res.json();
  } catch (err) {
    console.error('ApiService', 'Error de conexión al hacer POST de facturación ooss');
    // console.info(data, files);
    console.info(err);
    throw err;
  }
}
